import { useMemo } from 'react';
import { useLocation, matchPath } from '@react-router';
import { PricingApiCallSource } from '@apps/stays/types';
import { HotelSearchSource } from '@shared/constants/accommodations';

/**
 * Automatically determines the pricing API call source based on route patterns.
 *
 * Detection logic (checked in order of specificity):
 * 1. /:eventHandle/edit/planning/accommodations → admin_accommodation
 * 2. /:eventHandle/edit/accommodations → admin_accommodation
 * 3. /:eventHandle/accommodations?source=guest_map → guest_map
 * 4. /:eventHandle/accommodations → guest_accommodation
 *
 * @param contextHint - Optional manual override to force specific context
 * @returns PricingApiCallSource enum value
 */
export const usePricingCallSource = (contextHint?: PricingApiCallSource): PricingApiCallSource => {
  const location = useLocation();

  return useMemo(() => {
    if (contextHint) {
      return contextHint;
    }

    const pathname = location.pathname;
    const searchParams = new URLSearchParams(location.search);

    if (
      matchPath(pathname, {
        path: '/:eventHandle/edit/planning/accommodations',
        exact: false,
        strict: false,
      })
    ) {
      return PricingApiCallSource.AdminAccommodation;
    }

    if (
      matchPath(pathname, {
        path: '/:eventHandle/edit/accommodations',
        exact: true,
        strict: false,
      })
    ) {
      return PricingApiCallSource.AdminAccommodation;
    }

    if (
      matchPath(pathname, {
        path: '/:eventHandle/accommodations',
        exact: true,
        strict: false,
      })
    ) {
      return searchParams.get('source') === HotelSearchSource.GUEST_MAP ? PricingApiCallSource.GuestMap : PricingApiCallSource.GuestAccommodation;
    }

    return PricingApiCallSource.GuestAccommodation;
  }, [contextHint, location.pathname, location.search]);
};
